import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface ClientInfo {
  id: string;
  name: string;
}

interface ClientContextType {
  currentClientId: string | null;
  currentClientName: string | null;
  availableClients: ClientInfo[];
  loading: boolean;
  error: string | null;
  canSwitchClients: boolean;
  showAllClients: boolean;
  switchClient: (clientId: string) => void;
  setShowAllClients: (show: boolean) => void;
  invalidateClientQueries: () => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (typeof context === 'undefined') {
    // Return a fallback instead of throwing immediately
    return {
      currentClientId: null,
      currentClientName: null,
      availableClients: [],
      loading: true,
      error: 'ClientContext não inicializado',
      canSwitchClients: false,
      showAllClients: false,
      switchClient: () => {},
      setShowAllClients: () => {},
      invalidateClientQueries: () => {},
    };
  }
  return context;
};

interface ClientProviderProps {
  children: React.ReactNode;
}

// Query keys que devem ser invalidadas ao mudar de cliente
const CLIENT_DEPENDENT_QUERY_KEYS = [
  'dashboard-tickets',
  'dashboard-stats',
  'dashboard-sla-metrics',
  'dashboard-response-times',
  'dashboard-priority-distribution',
  'dashboard-trend-data',
  'dashboard-top-categories',
  'dashboard-top-analysts',
  'dashboard-satisfaction',
  'open-tickets',
  'resolved-today-tickets',
  'sla-risk-tickets',
  'my-group-tickets',
  'solicitacoes',
  'requisicoes',
  'incidentes',
  'incidentes-standard',
  'problemas',
  'mudancas',
  'contratos',
  'contratos-secure',
  'users-secure',
  'grupos',
  'categorias',
  'slas',
  'formularios',
  'planos-acao',
  'cmdb-ativos',
  'ativos',
  'reports',
  'advanced-reports',
  'sla-performance',
  'kb-articles',
  'softwares',
];

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const { user, profile, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [currentClientId, setCurrentClientId] = useState<string | null>(null);
  const [currentClientName, setCurrentClientName] = useState<string | null>(null);
  const [availableClients, setAvailableClients] = useState<ClientInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllClients, setShowAllClientsState] = useState(false);
  
  // Ref para rastrear o clientId anterior e evitar invalidações desnecessárias
  const previousClientIdRef = useRef<string | null>(null);
  const previousShowAllRef = useRef<boolean>(false);
  const isInitialMount = useRef(true);
  const invalidationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isReturningFromHiddenRef = useRef(false);

  // Determinar se o usuário pode alternar entre clientes
  const canSwitchClients = 
    (profile?.role === 'technician' || profile?.role === 'admin' || profile?.role === 'super_admin') &&
    availableClients.length > 1;

  // Listener para detectar troca de aba e prevenir invalidação desnecessária
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Aba ficou oculta
        isReturningFromHiddenRef.current = false;
      } else {
        // Aba ficou visível - marcar para prevenir invalidação
        isReturningFromHiddenRef.current = true;
        
        // Após 2 segundos, permitir invalidações novamente
        setTimeout(() => {
          isReturningFromHiddenRef.current = false;
        }, 2000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.email]);

  // Função para invalidar todas as queries dependentes de cliente
  const invalidateClientQueries = useCallback(() => {
    // Prevenir invalidação se acabou de voltar para aba
    if (isReturningFromHiddenRef.current) {
      return;
    }

    // Debounce de 500ms para evitar invalidações múltiplas
    if (invalidationTimeoutRef.current) {
      clearTimeout(invalidationTimeoutRef.current);
    }
    
    invalidationTimeoutRef.current = setTimeout(() => {
      // Invalidar todas as queries que dependem de client_id
      CLIENT_DEPENDENT_QUERY_KEYS.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      
      // Também invalidar queries que contêm o clientId no queryKey
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          // Invalidar se o queryKey contiver um UUID (provavelmente client_id)
          return Array.isArray(queryKey) && queryKey.some(
            (k) => typeof k === 'string' && 
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(k)
          );
        }
      });
    }, 500);
  }, [queryClient]);

  // Função para alternar cliente (para técnicos/admins)
  const switchClient = useCallback((newClientId: string) => {
    if (!canSwitchClients) {
      console.warn('Usuário não tem permissão para alternar clientes');
      return;
    }

    // Valor especial "all" para mostrar todos os clientes
    if (newClientId === 'all') {
      setShowAllClientsState(true);
      setCurrentClientId(null);
      setCurrentClientName('Todos os Clientes');
      invalidateClientQueries();
      return;
    }

    const targetClient = availableClients.find(c => c.id === newClientId);
    if (!targetClient) {
      console.warn('Cliente não encontrado na lista de clientes disponíveis');
      return;
    }
    
    // Atualizar estado
    setShowAllClientsState(false);
    setCurrentClientId(newClientId);
    setCurrentClientName(targetClient.name);
    
    // Invalidar cache imediatamente
    invalidateClientQueries();
  }, [canSwitchClients, availableClients, currentClientId, invalidateClientQueries]);

  // Função para alternar modo "Todos os Clientes"
  const setShowAllClients = useCallback((show: boolean) => {
    if (!canSwitchClients) return;
    
    setShowAllClientsState(show);
    if (show) {
      setCurrentClientId(null);
      setCurrentClientName('Todos os Clientes');
    }
    invalidateClientQueries();
  }, [canSwitchClients, invalidateClientQueries]);

  // Efeito para invalidar cache quando clientId ou showAllClients muda (exceto na montagem inicial)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousClientIdRef.current = currentClientId;
      previousShowAllRef.current = showAllClients;
      return;
    }

    // Verificar se houve mudança REAL (não apenas re-render)
    const clientChanged = previousClientIdRef.current !== currentClientId && 
                         previousClientIdRef.current !== null &&
                         currentClientId !== null;
    const showAllChanged = previousShowAllRef.current !== showAllClients;
    
    if (clientChanged || showAllChanged) {
      invalidateClientQueries();
    }

    previousClientIdRef.current = currentClientId;
    previousShowAllRef.current = showAllClients;
  }, [currentClientId, showAllClients, invalidateClientQueries]);

  useEffect(() => {
    const fetchUserClient = async () => {
      // Se ainda está carregando auth, não fazer nada
      if (authLoading) {
        return;
      }
      
      if (!user?.id) {
        setCurrentClientId(null);
        setCurrentClientName(null);
        setAvailableClients([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Buscar o client_id e client_ids do usuário na tabela users
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select(`
            client_id,
            client_ids,
            clients:client_id (
              id,
              name
            )
          `)
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Error fetching user client:', userError);
          setError('Erro ao carregar informações do cliente');
          return;
        }

        // Coletar todos os IDs de clientes
        const clientIds = new Set<string>();
        let primaryClientId: string | null = null;
        
        if (userData?.client_id) {
          clientIds.add(userData.client_id);
          primaryClientId = userData.client_id;
        }
        
        if (userData?.client_ids) {
          const multipleIds = Array.isArray(userData.client_ids) ? userData.client_ids : 
            (typeof userData.client_ids === 'string' ? JSON.parse(userData.client_ids) : []);
          multipleIds.forEach((id: string) => clientIds.add(id));
          
          // Se não tem client_id principal, usar o primeiro de client_ids
          if (!primaryClientId && multipleIds.length > 0) {
            primaryClientId = multipleIds[0];
          }
        }

        // Para admin/super_admin, buscar todos os clientes disponíveis
        if (profile?.role === 'admin' || profile?.role === 'super_admin') {
          const { data: allClients, error: allClientsError } = await supabase
            .from('clients')
            .select('id, name')
            .eq('status', 'active')
            .order('name');

          if (!allClientsError && allClients) {
            setAvailableClients(allClients);
          }
        } else if (clientIds.size > 0) {
          // Para outros usuários, buscar apenas seus clientes
          const { data: clientsData, error: clientsError } = await supabase
            .from('clients')
            .select('id, name')
            .in('id', Array.from(clientIds));

          if (!clientsError && clientsData) {
            setAvailableClients(clientsData);
            
            // Definir nome do cliente atual
            const currentClient = clientsData.find(c => c.id === primaryClientId);
            if (currentClient) {
              setCurrentClientName(currentClient.name);
            }
          }
        }

        setCurrentClientId(primaryClientId);
        
        // Se não definiu nome ainda (pode acontecer com admin)
        if (!currentClientName && primaryClientId) {
          const client = availableClients.find(c => c.id === primaryClientId);
          if (client) {
            setCurrentClientName(client.name);
          }
        }

      } catch (err) {
        console.error('Error in fetchUserClient:', err);
        setError('Erro inesperado ao carregar cliente');
      } finally {
        setLoading(false);
      }
    };

    fetchUserClient();
  }, [user?.id, authLoading, profile?.role]);

  const value: ClientContextType = {
    currentClientId: showAllClients ? null : currentClientId,
    currentClientName,
    availableClients,
    loading,
    error,
    canSwitchClients,
    showAllClients,
    switchClient,
    setShowAllClients,
    invalidateClientQueries,
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};
